import { useEffect, useState, useCallback, Fragment } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { Page, PageHeader, StatusBadge } from "@/components/Bits";
import MonthEndBar from "@/components/MonthEndBar";
import BalanceVariancePage from "@/pages/BalanceVariancePage";
import IntegrityPanel from "@/components/IntegrityPanel";
import { toast } from "sonner";
import { ArrowsClockwise, Buildings, Flask, Broom, CheckCircle, WarningCircle, MagnifyingGlass, X, CalendarBlank, Lock, CaretRight, FilePdf, MicrosoftExcelLogo } from "@phosphor-icons/react";

const TYPE_LABELS = { PREPAYMENT: "Prepayments", ACCRUAL: "Accruals", DEFERRED_INCOME: "Deferred Income", DEPRECIATION: "Depreciation" };
const VALID_TYPES = ["PREPAYMENT", "ACCRUAL", "DEFERRED_INCOME", "DEPRECIATION"];

// Reconciliation is the single hub. "All balances" is the read-only tie-out across every
// category; the category tabs render the actionable variance view (fix / accept) for that type.
const TABS = [
  { key: "all", label: "All balances" },
  { key: "accrual", label: "Accruals" },
  { key: "prepayment", label: "Prepayments" },
  { key: "deferred-income", label: "Deferred Income" },
  { key: "depreciation", label: "Depreciation" },
];

function fmtAsAt(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function Reconciliation() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "all";
  const setTab = (key) => setParams(key === "all" ? {} : { tab: key });
  const lockedType = VALID_TYPES.includes(params.get("type")) ? params.get("type") : null;
  const typeQ = lockedType ? `type=${lockedType}` : "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const qs = [refresh ? "refresh=true" : "", typeQ].filter(Boolean).join("&");
      const { data } = await api.get(`/reconciliation${qs ? `?${qs}` : ""}`);
      setData(data);
    } finally { setLoading(false); }
  }, [typeQ]);

  useEffect(() => { load(); }, [load]);

  const simulate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reconciliation/simulate", {});
      toast.warning(`Simulated external Xero entry of ${money(data.amount)} on ${data.code}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not simulate entry");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reconciliation/reset");
      toast.success(`Cleared ${data.cleared} simulated entr${data.cleared === 1 ? "y" : "ies"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not clear entries");
    } finally { setBusy(false); }
  };

  const sum = data?.summary;
  const allRows = data?.reconciliation || [];
  const q = search.trim().toLowerCase();
  const rows = q
    ? allRows.filter((r) =>
        (r.code || "").toLowerCase().includes(q) ||
        (r.account_name || "").toLowerCase().includes(q))
    : allRows;
  const tenants = [...new Set(rows.map((r) => r.tenant_name))];

  return (
    <Page>
      <PageHeader overline="Month-end" title="Reconciliation"
        subtitle="Tie each Xero nominal-code balance back to the live register. Variances are flagged, and can be fixed or accepted per category.">
        {tab === "all" && (<>
        {lockedType && (
          <span data-testid="type-lock-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold text-orangeink bg-orange/10 border border-orange/30 px-2.5 py-1 rounded-lg">
            <Lock size={12} weight="fill" /> {TYPE_LABELS[lockedType]} only
          </span>
        )}
        <button onClick={simulate} disabled={busy} data-testid="simulate-variance-btn"
          className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:border-navy hover:bg-navy/5 transition-all disabled:opacity-60">
          <Flask size={16} /> Simulate Xero entry
        </button>
        <button onClick={reset} disabled={busy} data-testid="reset-recon-btn"
          className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:border-navy hover:bg-navy/5 transition-all disabled:opacity-60">
          <Broom size={16} /> Clear simulated
        </button>
        <button onClick={() => load(true)} disabled={busy} data-testid="refresh-recon-btn"
          className="flex items-center gap-2 bg-orange text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy transition-colors duration-200 disabled:opacity-60">
          <ArrowsClockwise size={16} /> Refresh
        </button>
        </>)}
      </PageHeader>

      <div className="mb-6" data-testid="recon-month-end-bar">
        <MonthEndBar />
      </div>

      <div className="flex flex-wrap gap-1.5 mb-6 border-b border-slate-200" data-testid="recon-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`recon-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === t.key ? "border-orange text-navy" : "border-transparent text-navy/55 hover:text-navy"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab !== "all" ? (
        <BalanceVariancePage category={tab} embedded />
      ) : (<>
      {sum?.as_at && !loading && (
        <div data-testid="recon-as-at" className="flex items-center gap-1.5 -mt-2 mb-6 text-sm text-navy/70">
          <CalendarBlank size={15} className="text-navy/65" />
          Balances as at <span className="font-medium text-navy/75">{fmtAsAt(sum.as_at)}</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
        <Stat label="Codes Checked" value={sum?.total_codes ?? "—"} id="recon-total" />
        <Stat label="Reconciled" value={sum?.reconciled ?? "—"} accent="positive" icon={CheckCircle} id="recon-ok" />
        <Stat label="Variances" value={sum?.variances ?? "—"} accent={sum?.variances ? "negative" : "navy"} icon={WarningCircle} id="recon-var" />
        <Stat label="Total Variance" value={sum ? money(sum.total_variance) : "—"} accent={sum?.total_variance ? "negative" : "navy"} mono id="recon-total-var" />
      </div>

      {sum && sum.variances === 0 && !loading && (
        <div data-testid="recon-all-clear"
          className="flex items-center gap-3 bg-positive/10 border border-positive/30 text-positive rounded-lg px-5 py-3.5 mb-6">
          <CheckCircle size={22} weight="fill" />
          <span className="text-sm font-medium">All nominal-code balances tie back to the register. Nothing to action.</span>
        </div>
      )}

      {sum?.balance_source === "estimate" && !loading && (
        <div data-testid="recon-estimate-banner"
          className="flex items-start gap-3 bg-warn/10 border border-warn/40 text-navy rounded-lg px-5 py-3.5 mb-6">
          <WarningCircle size={22} weight="fill" className="text-warn shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold">Showing estimated balances, not Xero's Trial Balance.</span>{" "}
            The Xero Balance column is derived from invoices and journals because the live Trial Balance
            could not be read. This usually means the <b>reports permission</b> hasn't been granted on your
            current connection. Go to <b>Settings → Connect to Xero</b>, click <b>Disconnect</b> then
            <b> Connect</b> again and approve the consent screen to enable real Trial Balance figures.
            {sum?.balance_source_detail && (
              <div data-testid="recon-estimate-detail" className="mt-2 font-mono text-xs text-navy/70 bg-white/60 rounded px-2 py-1">
                Reason: {sum.balance_source_detail}
              </div>
            )}
          </div>
        </div>
      )}
      {sum?.balance_source === "trial_balance" && !loading && (
        <div data-testid="recon-tb-badge" className="flex items-center gap-2 mb-6 text-xs text-navy/70">
          <CheckCircle size={15} weight="fill" className="text-positive" />
          Xero Balance sourced from the live Trial Balance.
        </div>
      )}

      <div className="flex justify-end mb-4">
        <div className="relative w-full sm:w-80">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/35" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="recon-search"
            placeholder="Search code or account…"
            className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-8 py-1.5 text-xs text-navy placeholder:text-navy/35 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-colors" />
          {search && (
            <button onClick={() => setSearch("")} data-testid="recon-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-navy/35 hover:text-navy">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
              <Th>Code</Th><Th>Account</Th><Th>Type</Th>
              <Th right>Xero Balance</Th><Th right>Register Balance</Th><Th right>Variance</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tn) => (
              <RowsForTenant key={tn} tenant={tn} rows={rows.filter((r) => r.tenant_name === tn)} />
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && (
          <div className="py-16 text-center text-navy/70" data-testid="recon-empty">
            {allRows.length === 0 ? (
              <>
                <div className="font-heading font-bold text-lg text-navy">Nothing to reconcile yet</div>
                <p className="text-sm mt-1">Import items and create schedules first.</p>
              </>
            ) : (
              <>
                <div className="font-heading font-bold text-lg text-navy">No matching codes</div>
                <p className="text-sm mt-1">Try a different search term.</p>
              </>
            )}
          </div>
        )}
        {loading && <div className="py-16 text-center font-mono text-navy/65 text-sm tracking-widest">LOADING_DATA…</div>}
      </div>

      <PLMovementPanel typeQ={typeQ} />
      <IntegrityPanel />
      </>)}
    </Page>
  );
}

const LEDGER_ROUTE = {
  PREPAYMENT: "/prepayments", ACCRUAL: "/accruals",
  DEFERRED_INCOME: "/deferred-income", DEPRECIATION: "/fixed-assets",
};

function RowsForTenant({ tenant, rows }) {
  const navigate = useNavigate();
  const openLedger = (r) => {
    const base = LEDGER_ROUTE[r.category] || "/register";
    navigate(`${base}?code=${encodeURIComponent(r.code)}`);
  };
  const CAT_ORDER = ["PREPAYMENT", "ACCRUAL", "DEFERRED_INCOME", "DEPRECIATION"];
  const cats = [...CAT_ORDER.filter((c) => rows.some((r) => r.category === c)),
                ...[...new Set(rows.map((r) => r.category))].filter((c) => c && !CAT_ORDER.includes(c))];
  const sumBy = (g, k) => g.reduce((a, r) => a + (r[k] || 0), 0);
  return (
    <>
      <tr className="bg-navy/[0.03]">
        <td colSpan={7} className="px-5 py-2">
          <div className="flex items-center gap-2 overline text-navy/70">
            <Buildings size={14} /> {tenant}
          </div>
        </td>
      </tr>
      {cats.map((cat) => {
        const g = rows.filter((r) => r.category === cat);
        const ccy = g[0]?.currency;
        return (
          <Fragment key={`${tenant}-${cat}`}>
            <tr className="bg-slate-100/60 border-y border-slate-200" data-testid={`recon-group-${cat}`}>
              <td colSpan={7} className="px-5 py-2">
                <span className="overline text-navy/70 font-semibold">{TYPE_LABELS[cat] || cat}</span>
                <span className="ml-2 text-xs text-navy/50">{g.length} code{g.length === 1 ? "" : "s"}</span>
              </td>
            </tr>
            {g.map((r) => (
              <tr key={`${r.tenant_id}-${r.code}`} data-testid={`recon-row-${r.tenant_id}-${r.code}`}
                onClick={() => openLedger(r)}
                title={`Open in ledger to review ${r.code}`}
                className={`group border-b border-slate-100 cursor-pointer transition-colors ${r.status === "Variance" ? "bg-negative/[0.03] hover:bg-negative/[0.07]" : "hover:bg-slate-50"}`}>
                <Td mono className="font-semibold">
                  <span className="inline-flex items-center gap-2">
                    {r.code}
                    {r.has_manual && (
                      <span data-testid={`recon-manual-${r.tenant_id}-${r.code}`} title="Includes app-created adjustments"
                        className="text-[0.6rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-orange/10 text-orangeink border border-orange/30">
                        Manual
                      </span>
                    )}
                  </span>
                </Td>
                <Td>{r.account_name}</Td>
                <Td><StatusBadge value={r.category} /></Td>
                <Td right mono>{money(r.xero_balance, r.currency)}</Td>
                <Td right mono>{money(r.register_balance, r.currency)}</Td>
                <Td right mono className={r.status === "Variance" ? "text-negative font-semibold" : "text-navy/65"}>
                  {money(r.variance, r.currency)}
                </Td>
                <Td>
                  <span className="inline-flex items-center gap-2">
                    <StatusBadge value={r.status} testid={`recon-status-${r.tenant_id}-${r.code}`} />
                    <CaretRight size={14} weight="bold" className="text-navy/25 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                </Td>
              </tr>
            ))}
            <tr className="bg-slate-50 border-b border-slate-200 font-semibold" data-testid={`recon-subtotal-${cat}`}>
              <td colSpan={3} className="px-5 py-2.5 text-right overline text-navy/60">{TYPE_LABELS[cat] || cat} subtotal</td>
              <td className="px-5 py-2.5 text-right font-mono tabular text-navy" data-testid={`recon-subtotal-xero-${cat}`}>{money(sumBy(g, "xero_balance"), ccy)}</td>
              <td className="px-5 py-2.5 text-right font-mono tabular text-navy" data-testid={`recon-subtotal-register-${cat}`}>{money(sumBy(g, "register_balance"), ccy)}</td>
              <td className="px-5 py-2.5 text-right font-mono tabular text-navy" data-testid={`recon-subtotal-variance-${cat}`}>{money(sumBy(g, "variance"), ccy)}</td>
              <td />
            </tr>
          </Fragment>
        );
      })}
    </>
  );
}

function PLMovementPanel({ typeQ }) {
  const navigate = useNavigate();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [hasAny, setHasAny] = useState(false);
  const [dling, setDling] = useState("");
  const [drillCode, setDrillCode] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const rangeQs = useCallback(() => {
    const p = [];
    if (typeQ) p.push(typeQ);
    if (start) p.push(`start=${start}`);
    if (end) p.push(`end=${end}`);
    return p.length ? `?${p.join("&")}` : "";
  }, [typeQ, start, end]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get(`/pl-movement${rangeQs()}`);
      setData(data);
      if ((data.by_code || []).length) setHasAny(true);
    } catch { /* silent */ }
  }, [rangeQs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDrill = async (code) => {
    setDrillCode(code); setDrill(null); setDrillLoading(true);
    try {
      const p = [`code=${encodeURIComponent(code)}`];
      if (start) p.push(`start=${start}`); if (end) p.push(`end=${end}`);
      const { data } = await api.get(`/pl-movement/schedules?${p.join("&")}`);
      setDrill(data);
    } catch { toast.error("Could not load schedules for this code"); }
    finally { setDrillLoading(false); }
  };

  const download = async (kind) => {
    setDling(kind);
    try {
      const res = await api.get(`/pl-movement/${kind}${rangeQs()}`, { responseType: "blob" });
      const type = kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url; a.download = `released-to-pl-by-code.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`P&L movement ${kind.toUpperCase()} downloaded`);
    } catch { toast.error(`Could not generate ${kind.toUpperCase()}`); }
    finally { setDling(""); }
  };

  const rows = data?.by_code || [];
  if (!hasAny && rows.length === 0 && !start && !end) return null;

  const iso = (d) => d.toISOString().slice(0, 10);
  const setPreset = (kind) => {
    const n = new Date();
    if (kind === "month") {
      setStart(iso(new Date(n.getFullYear(), n.getMonth(), 1)));
      setEnd(iso(new Date(n.getFullYear(), n.getMonth() + 1, 0)));
    } else if (kind === "lastmonth") {
      setStart(iso(new Date(n.getFullYear(), n.getMonth() - 1, 1)));
      setEnd(iso(new Date(n.getFullYear(), n.getMonth(), 0)));
    } else if (kind === "quarter") {
      const q = Math.floor(n.getMonth() / 3);
      setStart(iso(new Date(n.getFullYear(), q * 3, 1)));
      setEnd(iso(new Date(n.getFullYear(), q * 3 + 3, 0)));
    } else if (kind === "year") {
      setStart(iso(new Date(n.getFullYear(), 0, 1)));
      setEnd(iso(new Date(n.getFullYear(), 11, 31)));
    } else { setStart(""); setEnd(""); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden mt-6" data-testid="pl-movement-panel">
      <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-heading font-bold text-navy">Released to P&amp;L by code</div>
          <p className="text-xs text-navy/60 mt-0.5">Posted releases apportioned to each P&amp;L code (split-aware). Click a row for the schedules feeding it.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-1 mr-1" data-testid="pl-movement-presets">
            {[{ k: "month", l: "This month" }, { k: "lastmonth", l: "Last month" }, { k: "quarter", l: "This quarter" }, { k: "year", l: "This year" }].map((p) => (
              <button key={p.k} onClick={() => setPreset(p.k)} data-testid={`pl-movement-preset-${p.k}`}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors">
                {p.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <CalendarBlank size={15} className="text-navy/50" />
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="pl-movement-start"
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-300" />
            <span className="text-navy/40">–</span>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="pl-movement-end"
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-300" />
            {(start || end) && (
              <button onClick={() => { setStart(""); setEnd(""); }} data-testid="pl-movement-clear-range"
                className="text-navy/50 hover:text-navy px-1" title="Clear range"><X size={14} /></button>
            )}
          </div>
          <button onClick={() => download("pdf")} disabled={!!dling} data-testid="pl-movement-pdf"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy hover:border-navy hover:bg-navy/5 transition-colors disabled:opacity-60">
            <FilePdf size={15} /> {dling === "pdf" ? "…" : "PDF"}
          </button>
          <button onClick={() => download("excel")} disabled={!!dling} data-testid="pl-movement-excel"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy hover:border-navy hover:bg-navy/5 transition-colors disabled:opacity-60">
            <MicrosoftExcelLogo size={15} /> {dling === "excel" ? "…" : "Excel"}
          </button>
        </div>
      </div>
      <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
        <span className="text-xs text-navy/60">{(start || end) ? "Period movement" : "All posted releases to date"}</span>
        <div className="text-right">
          <span className="overline text-navy/60 mr-2">Total released</span>
          <span className="font-mono text-base font-semibold text-navy tabular" data-testid="pl-movement-total">{money(data?.total || 0, data?.currency)}</span>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="py-12 text-center text-navy/55 text-sm" data-testid="pl-movement-empty">No posted releases in this period.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white border-b border-slate-200">
              <Th>P&amp;L Code</Th><Th>Type</Th><Th right>Released</Th><Th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.pl_account_code} data-testid={`pl-movement-row-${r.pl_account_code}`}
                onClick={() => openDrill(r.pl_account_code)}
                className="group border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                <Td mono className="font-semibold">{r.pl_account_code}{r.pl_account_name ? ` — ${r.pl_account_name}` : ""}</Td>
                <Td><StatusBadge value={r.category} /></Td>
                <Td right mono className="text-navy font-semibold">{money(r.released_to_date, data.currency)}</Td>
                <Td right><CaretRight size={14} weight="bold" className="text-navy/25 group-hover:text-orange transition-colors" /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {drillCode && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="pl-movement-drill">
          <div className="absolute inset-0 bg-navy/30 backdrop-blur-sm" onClick={() => setDrillCode(null)} />
          <div className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col">
            <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100">
              <div>
                <div className="overline text-navy/60">Schedules feeding</div>
                <div className="font-heading font-bold text-navy text-lg">{drillCode}{drill?.pl_account_name ? ` — ${drill.pl_account_name}` : ""}</div>
                {drill && <div className="text-sm text-navy/65 mt-1">{drill.schedules.length} schedule(s) · {money(drill.total, drill.currency)} released{(start || end) ? " in period" : " to date"}</div>}
              </div>
              <button onClick={() => setDrillCode(null)} data-testid="pl-drill-close" className="text-slate-500 hover:text-navy"><X size={22} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {drillLoading && <div className="py-16 text-center font-mono text-navy/60 text-sm tracking-widest">LOADING…</div>}
              {drill && drill.schedules.length === 0 && (
                <div className="py-16 text-center text-navy/60 text-sm" data-testid="pl-drill-empty">No posted releases to this code in this period.</div>
              )}
              {drill && drill.schedules.map((s) => (
                <button key={s.schedule_id} data-testid={`pl-drill-schedule-${s.schedule_id}`}
                  onClick={() => navigate(`/register?open=${s.schedule_id}`)}
                  className="w-full text-left border border-slate-200 rounded-xl px-4 py-3 mb-2.5 hover:border-orange hover:bg-orange/[0.03] transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-navy truncate flex items-center gap-2">
                        {s.reference || "—"}
                        {s.is_split && <span className="text-[0.6rem] font-semibold uppercase px-1.5 py-0.5 rounded-md bg-orange/10 text-orangeink border border-orange/30">Split</span>}
                        {s.manual && <span className="text-[0.6rem] font-semibold uppercase px-1.5 py-0.5 rounded-md bg-navy/[0.06] text-navy/60">Manual</span>}
                      </div>
                      <div className="text-xs text-navy/60 truncate">{s.description || TYPE_LABELS[s.category] || s.category} · from {s.source_account_code}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono font-semibold text-navy">{money(s.released_to_code, drill.currency)}</div>
                      <CaretRight size={14} weight="bold" className="text-navy/25 inline" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "navy", mono, icon: Icon, id }) {
  const color = accent === "positive" ? "text-positive" : accent === "negative" ? "text-negative" : "text-navy";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-5" data-testid={id}>
      <div className="flex items-center justify-between">
        <div className="overline text-navy/65">{label}</div>
        {Icon && <Icon size={18} className={color} />}
      </div>
      <div className={`${mono ? "font-mono text-2xl" : "font-heading font-black text-4xl tracking-tighter"} mt-2 tabular ${color}`}>
        {value}
      </div>
    </div>
  );
}

const Th = ({ children, right }) => <th className={`px-5 py-3 ${right ? "text-right" : "text-left"} font-semibold whitespace-nowrap text-[0.66rem] uppercase tracking-wider text-slate-600 bg-slate-50`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) =>
  <td className={`px-5 py-3.5 ${right ? "text-right" : "text-left"} ${mono ? "font-mono tabular" : ""} ${className}`}>{children}</td>;
